import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import CryptoJS from "crypto-js";
import styles from "./ResetPassword.module.scss";
import { api } from "../../services/api";
import { ApiError } from "../../services/api/BaseApi";
import { getPasswordStrengthResult, passwordRules } from "../../utils/passwordValidation";
import PasswordInput from "../../components/PasswordInput/PasswordInput";

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>();

  const password = watch("password");

  const onSubmit = async (data: ResetPasswordForm) => {
    setError(null);

    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }

    try {
      const hashedPassword = CryptoJS.SHA256(data.password).toString();
      await api.app.resetPassword(token, hashedPassword);
      setSuccess(true);
    } catch (err: any) {
      if (err instanceof ApiError) {
        setError(err.data?.message || err.message || "Something went wrong.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
  };

  // No token in the URL
  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <span className={styles.cursive}>Hippocampus Gaming</span>
            <p className={styles.tagline}>Your gaming universe, all in one place</p>
          </div>
          <div className={styles.divider}>Invalid link</div>
          <div className={styles.errorMessage}>
            <p>This password reset link is invalid or has expired.</p>
            <a href="/forgot-password" className={styles.backLink}>
              Request a new reset link
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.cursive}>Hippocampus Gaming</span>
          <p className={styles.tagline}>Your gaming universe, all in one place</p>
        </div>

        <div className={styles.divider}>Reset password</div>

        {success ? (
          <div className={styles.successMessage}>
            <p>Your password has been reset successfully.</p>
            <a href="/signin" className={styles.backLink}>
              Sign in with your new password
            </a>
          </div>
        ) : (
          <>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.inputGroup}>
                <label htmlFor="password">New password</label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  style={{ fontSize: "16px" }}
                  {...register("password", passwordRules)}
                />
                {errors.password && (
                  <span className={styles.error}>{errors.password.message}</span>
                )}

                {/* Strength Meter */}
                {password && (
                  <div className={styles.strengthMeter}>
                    <div className={styles.strengthTrack}>
                      <div
                        className={styles.strengthBar}
                        style={{
                          width: `${(getPasswordStrengthResult(password).score / 5) * 100}%`,
                          backgroundColor: getPasswordStrengthResult(password).color,
                        }}
                      />
                    </div>
                    <span
                      className={styles.strengthLabel}
                      style={{ color: getPasswordStrengthResult(password).color }}
                    >
                      {getPasswordStrengthResult(password).label}
                    </span>
                  </div>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="confirmPassword">Confirm password</label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="••••••••"
                  style={{ fontSize: "16px" }}
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value) =>
                      value === password || "Passwords do not match",
                  })}
                />
                {errors.confirmPassword && (
                  <span className={styles.error}>
                    {errors.confirmPassword.message}
                  </span>
                )}
              </div>

              {error && <span className={styles.error}>{error}</span>}

              <button
                type="submit"
                className={styles.button}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Resetting..." : "Reset password"}
              </button>
            </form>

            <p className={styles.footer}>
              Remember your password? <a href="/signin">Sign in</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
